import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {environment} from "../../../environments/environment";
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class HotelService {

  private hotelsMap = new Map<string, any>();

  constructor(private http:HttpClient) { }

  getBase(){
    const url=`${environment.customerPath}customers/base`;
    const params:HttpParams= new HttpParams();
    return this.http.get(url,{params: params})
  }

  getHotel(hotel_id): Observable<any> {
    const url = `${environment.customerPath}customers/${hotel_id}/show`;
    return this.http.get(url).pipe(
      tap(hotelData => {
        this.hotelsMap.set(hotel_id, hotelData);
        this.syncCountryCodeToLocalStorage(hotel_id, hotelData);
      })
    );
  }

  private syncCountryCodeToLocalStorage(hotelId: any, showData: any): void {
    const code = showData?.country?.code2;
    if (!code || code === 'none') return;

    for (const key of ['currentHotel', 'lastHotelUsed']) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw || raw === 'null') continue;
        const stored = JSON.parse(raw);
        if (stored && String(stored.id) === String(hotelId) && (!stored.country_code || stored.country_code === 'none')) {
          stored.country_code = code;
          localStorage.setItem(key, JSON.stringify(stored));
        }
      } catch { }
    }
  }

  getHotelById(hotel_id: string): any {
    return this.hotelsMap.get(hotel_id);
  }

  setHotel(hotel_id: string, hotelData: any): void {
    this.hotelsMap.set(hotel_id, hotelData);
  }

  hasHotel(hotel_id: string): boolean {
    return this.hotelsMap.has(hotel_id);
  }

  getCities(contry_code:string){    
    const url=`${environment.customerPath}commons/country/${contry_code}/cities`;
    const params:HttpParams= new HttpParams();
    return this.http.get(url, {params: params})
  }

  getCity(cityId:string){
    const url=`${environment.customerPath}commons/city/${cityId}`;
    const params:HttpParams = new HttpParams();
    return this.http.get(url, {params: params})
  }

  updateHotel(hotel){
    const url=`${environment.customerPath}customers/${hotel.id}/edit`;
    return this.http.put(url,hotel)
  }

  createHotel(hotel){
    const url=`${environment.customerPath}customers/new`;
    return this.http.post(url,hotel)
  }

  availableCurrencies(currencies:Currency[]){
    const filter = currencies.filter(x=> !x.symbol.includes('?') && x.symbol!=="");
    return filter;
  }

  createChain(name){
    const url=`${environment.customerPath}commons/chain/name`;
    return this.http.post(url,name)
  }

  getGuestContactInfo(customer_id){
    const url=`${environment.customerPath}customers/${customer_id}/guest-contact-info`;
    return this.http.get(url)
  }

  putGuestContactInfo(customer_id, data){
    const url=`${environment.customerPath}customers/${customer_id}/guest-contact-info`;
    return this.http.put(url, data)
  }

  getAlertsExecptions(customerId: number) {
    const url = `${environment.customerPath}alerts/exceptions/customer/${customerId}`;
    return this.http.get(url);
  }

  putAlertsExecptions(data: { customer_id: number; exception_type: string; active: boolean }) {
    const url = `${environment.customerPath}alerts/exceptions`;
    return this.http.put(url, data);
  }

  getAreas(customerId: number): Observable<any[]> {
    const url = `${environment.customerPath}customers/${customerId}/configuration/areas`;
    return this.http.get<any[]>(url);
  }

  putAreas(customerId: number, data: any[]) {
    const url = `${environment.customerPath}customers/${customerId}/configuration/areas`;
    return this.http.put(url, data);
  }

  postGdprAnonymizeManual(customerIds: number[], email: string, phone: string): Observable<unknown> {
    const idsParam = customerIds?.length ? customerIds.join(',') : '';
    const e = (email ?? '').trim();
    const p = (phone ?? '').trim();
    const params = new HttpParams().set('customer_ids', idsParam).set('email', e).set('phone', p);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const url = `${environment.customerPath}gdpr/anonymize/manual`;
    return this.http.post(url, {}, { params, headers });
  }

  getGdprConfig(customerId: number | string): Observable<any> {
    const url = `${environment.customerPath}gdpr/config/customers/${customerId}`;
    return this.http.get(url);
  }

  putGdprConfig(
    customerId: number | string,
    body: { id: number; customer_id: number; days_after_checkout: number; active: boolean },
  ): Observable<any> {
    const url = `${environment.customerPath}gdpr/config/customers/${customerId}`;
    return this.http.put(url, body);
  }

  getGdprExecutions(params: {
    page?: number;
    page_size?: number;
    paged?: boolean;
    id?: string | number;
    status?: string | number;
  }): Observable<GdprExecutionsApiResponse> {
    const url = `${environment.customerPath}gdpr/executions/`;
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('page_size', String(params.page_size ?? 10))
      .set('paged', String(params.paged ?? true));
    if (params.id !== undefined && params.id !== null && `${params.id}` !== '') {
      httpParams = httpParams.set('id', String(params.id));
    }
    if (params.status !== undefined && params.status !== null && `${params.status}` !== '') {
      httpParams = httpParams.set('status', String(params.status));
    }
    return this.http.get<GdprExecutionsApiResponse>(url, { params: httpParams });
  }
}

export interface GdprExecutionItem {
  id: number;
  job_execution_id: string | null;
  execution_type_id: number;
  status_id: number;
  config_id: number | null;
  requested_data: string;
  fidelity_user_id: number;
  created_at: string;
  updated_at: string;
  customer_names: string;
}

export interface GdprExecutionsPageData {
  info: GdprExecutionItem[];
  page_number: number;
  total_pages: number;
  has_next: boolean;
  total_of_elements: number;
}

export interface GdprExecutionsApiResponse {
  data: GdprExecutionsPageData;
  errors: unknown[];
  status: string;
}

export interface Currency{
  id:number;
  country:string;
  name: string;
  symbol: string;
  code: string;
  unit: string;
}
